import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Lock, Wallet, ArrowDownToLine } from "lucide-react";
import { WithdrawForm } from "../components/WithdrawForm";
import { WithdrawalsList } from "../components/WithdrawalsList";
import { brl } from "../components/PurchaseDialog";

const Blocked = ({ cfg, pixConfirmed, processing }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="withdraw-blocked">
      <Lock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
      <span>
        {!pixConfirmed ? (<>Confirme sua conta Pix (CPF) no{" "}
          <button className="gold-text underline" onClick={() => navigate("/perfil")} data-testid="withdraw-go-profile">Perfil</button> para liberar o saque.</>)
          : !cfg.withdrawals_enabled ? `Saques bloqueados no momento${cfg.withdrawals_open_date ? ` — liberação em ${new Date(cfg.withdrawals_open_date).toLocaleDateString("pt-BR")}` : ""}. Você pode simular o valor abaixo, mas o pedido só poderá ser enviado após a liberação.`
          : processing && "Você já tem um saque em andamento (aguardando aprovação ou processando). Aguarde para pedir outro."}
      </span>
    </div>
  );
};

export default function Withdraw() {
  const [cfg, setCfg] = useState(null);
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setMe(r.data.user)).catch(() => {});
    api.get("/withdrawals").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    load();
  }, [load]);
  const processing = items.some((w) => ["processing", "pending_approval"].includes(w.status));
  useEffect(() => {
    if (!processing) { return undefined; }
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [processing, load]);

  const ready = cfg && me;
  const can = ready && cfg.withdrawals_enabled && me.pix_confirmed && !processing;
  const min = cfg?.withdrawal_min ?? 50;
  const feePct = cfg?.withdrawal_fee_percent ?? 2;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Saque via Pix</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1 mb-8">Solicitar <span className="gold-text">saque</span></h1>
        {!ready ? <p className="text-muted-foreground">Carregando...</p> : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <Card className="p-6 lg:col-span-2 gold-border h-fit" data-testid="withdraw-balance-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
                <Wallet className="h-4 w-4" /> Disponível para saque
              </div>
              <div className="mt-2 font-mono text-3xl font-bold gold-text" data-testid="withdraw-available">{brl(me.balance)}</div>
              <ul className="mt-4 space-y-1 text-xs text-muted-foreground">
                <li>Pix enviado <b className="text-foreground">somente para o CPF do titular</b>{me.pix_key ? ` (${me.pix_key})` : ""}.</li>
                <li>Valor mínimo: <b className="text-foreground">{brl(min)}</b></li>
                <li>Taxa de saque: <b className="text-foreground">{feePct.toLocaleString("pt-BR")}%</b></li>
                <li>Saldo Consumo não pode ser sacado.</li>
              </ul>
            </Card>
            <Card className="p-6 lg:col-span-3 space-y-4" data-testid="withdraw-request-card">
              <div className="flex items-center gap-2 font-display text-lg font-bold">
                <ArrowDownToLine className="h-5 w-5 text-amber-500" /> Solicitar Saque
              </div>
              {!can && <Blocked cfg={cfg} pixConfirmed={me.pix_confirmed} processing={processing} />}
              <WithdrawForm balance={me.balance} min={min} feePct={feePct} pixKey={me.pix_key} canSubmit={can} onDone={load} />
            </Card>
            <Card className="p-6 lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Meus saques</div>
              <WithdrawalsList items={items} />
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
